import * as React from "react";
import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import { Check } from "lucide-react";

import { cn } from "@/lib/utils";

/**
 * The shadcn/Radix checkbox primitive: an unlabelled, controllable box for
 * composing inside other Tailwind components (list rows, menus, popovers) where
 * the surrounding markup owns the label and the hit area.
 *
 * For a form field — label, hint, error text, react-hook-form wiring — use
 * `Core/Checkbox/CheckboxNew` instead.
 */
const Checkbox = React.forwardRef<
	React.ElementRef<typeof CheckboxPrimitive.Root>,
	React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>
>(({ className, ...props }, ref) => (
	<CheckboxPrimitive.Root
		ref={ref}
		className={cn(
			"peer h-4 w-4 shrink-0 rounded-sm border border-solid border-gray-400 bg-white shadow-sm outline-none",
			"focus-visible:ring-2 focus-visible:ring-action focus-visible:ring-offset-1",
			"disabled:cursor-not-allowed disabled:opacity-50",
			"data-[state=checked]:border-action data-[state=checked]:bg-action data-[state=checked]:text-white",
			className
		)}
		{...props}
	>
		<CheckboxPrimitive.Indicator className={cn("flex items-center justify-center text-current")}>
			<Check className="h-3.5 w-3.5" strokeWidth={3} />
		</CheckboxPrimitive.Indicator>
	</CheckboxPrimitive.Root>
));
Checkbox.displayName = CheckboxPrimitive.Root.displayName;

export { Checkbox };
